/* APC CartTelemetry v5 - deliberately fail-open. No cart promise is awaited or replaced.
 *
 * 2026-08-02 metrics fixes:
 *  1. dataLayer pushes are no longer rate limited. emit() previously sent every
 *     apc_cart event through allowed(), which drops a repeat of the same key for
 *     5 minutes. That is correct for an alert beacon and wrong for analytics: real
 *     repeat adds of the same variant were silently never counted. allowed() now
 *     gates only notifyFailure().
 *  2. Duplicate apc_cart events. installObservers() re-runs at 0/100/500/1500/5000/
 *     15000ms so cart failures stay observable when a third party replaces
 *     window.fetch. The old guard only inspected the outermost wrapper
 *     (window.fetch.__apcCartTelemetry), so if any app wrapped fetch AFTER us the
 *     next re-install wrapped that and left two APC wrappers in the chain, emitting
 *     every cart event twice. This matters more now that (1) removed the accidental
 *     de-duplication.
 *  3. One /cart.js request per emission instead of one per event name. cart_open +
 *     view_cart still share a read, while checkout events emit synchronously before
 *     navigation so the browser cannot unload before GTM sees them.
 *  4. Standard GTM custom-event triggers receive apc_cart through event as well as
 *     the legacy name field, and XHR failures emit exactly once.
 *
 * 2026-08-04 fix: (2)'s original claim() keyed on the response promise, assuming
 * every wrapper layer sees the same promise object. Verified live that assumption
 * is false on this storefront: Elevar and the InstantJS/Meta pixel wrapper both
 * wrap window.fetch and return a new promise via .then() instead of passing the
 * original through. A fetch-call marker now lives only while wrappers synchronously
 * invoke the underlying fetch. It survives promise-transforming layers without
 * permanently suppressing a later real call that reuses the same RequestInit.
 *
 * 2026-08-27 customized-product analytics fix:
 *  1. Successful add events include response-derived currency, total value, and
 *     physical line-item detail while keeping private customization text out.
 *  2. Elevar receives one customer-level add event for a parent + hidden Custom
 *     add-on burst. The visible parent carries the aggregate price, so Elevar's
 *     direct GA4 destination and its GTM Meta tag record the actual customized
 *     product value. Shopify Web Pixel destinations remain independently owned.
 */
(function (window, document) {
  'use strict';
  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var build = document.documentElement.getAttribute('data-apc-theme-build') || 'unknown';
  var sessionKey = 'apc_cart_telemetry_session';
  var session = '';
  try { session = window.sessionStorage.getItem(sessionKey) || ''; if (!session) { session = 'c_' + Math.random().toString(36).slice(2, 12); window.sessionStorage.setItem(sessionKey, session); } } catch (_) { session = 'volatile'; }
  var seen = Object.create(null), sent = [], MAX_PER_MINUTE = 20;
  function now() { return Date.now(); }
  function allowed(key) { var t = now(); sent = sent.filter(function (x) { return t - x < 60000; }); if (sent.length >= MAX_PER_MINUTE || (seen[key] && t - seen[key] < 300000)) return false; seen[key] = t; sent.push(t); return true; }
  function bodyContext(init) {
    var out = {};
    try {
      var body = init && init.body;
      if (typeof body !== 'string') return out;
      var payload = JSON.parse(body);
      var items = Array.isArray(payload.items) ? payload.items : null;
      if (!items) {
        out.item_count = payload.quantity || undefined;
        out.variant_id = payload.id;
        return out;
      }

      var visibleItem = items.find(function (item) {
        return !(item && item.properties && item.properties._cf_ === 'addonProduct');
      }) || items[0];
      out.line_count = items.length;
      out.item_count = items.reduce(function (total, item) {
        var quantity = Number(item && item.quantity);
        return total + (Number.isFinite(quantity) ? quantity : 0);
      }, 0);
      out.variant_id = visibleItem && visibleItem.id;
    } catch (_) {}
    return out;
  }
  function activeCurrency() {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active)
      || (window.theme && window.theme.current_iso_code)
      || 'USD';
    code = String(code || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : 'USD';
  }
  function addedItems(payload) {
    if (Array.isArray(payload)) return payload;
    if (payload && Array.isArray(payload.items)) return payload.items;
    return payload && payload.id ? [payload] : [];
  }
  function privateProperties(item) {
    var properties = item && item.properties;
    return properties && properties._cf_ === 'addonProduct';
  }
  function itemUnitPriceCents(item) {
    var quantity = Math.max(1, Number(item && item.quantity) || 1);
    var linePrice = Number(item && (item.final_line_price != null ? item.final_line_price : item.line_price));
    if (Number.isFinite(linePrice)) return linePrice / quantity;
    var unitPrice = Number(item && (item.final_price != null ? item.final_price : item.price));
    return Number.isFinite(unitPrice) ? unitPrice : 0;
  }
  function itemLinePriceCents(item) {
    var linePrice = Number(item && (item.final_line_price != null ? item.final_line_price : item.line_price));
    if (Number.isFinite(linePrice)) return linePrice;
    return itemUnitPriceCents(item) * (Math.max(1, Number(item && item.quantity) || 1));
  }
  function enrichAddContext(response, fallback) {
    if (!response || !response.ok || typeof response.clone !== 'function') return Promise.resolve(fallback);
    try {
      return response.clone().json().then(function (payload) {
        var physicalItems = addedItems(payload);
        if (!physicalItems.length) return fallback;
        var context = Object.assign({}, fallback || {});
        context.currency = activeCurrency();
        context.value = physicalItems.reduce(function (total, item) {
          return total + itemLinePriceCents(item);
        }, 0) / 100;
        context.items = physicalItems.map(function (item) {
          return {
            item_id: String((item && (item.sku || item.variant_id || item.id)) || ''),
            item_product_id: item && item.product_id,
            item_variant_id: item && (item.variant_id || item.id),
            item_name: String((item && (item.product_title || item.title)) || ''),
            item_variant: String((item && item.variant_title) || ''),
            item_brand: String((item && item.vendor) || ''),
            item_category: String((item && item.product_type) || ''),
            price: itemUnitPriceCents(item) / 100,
            quantity: Math.max(1, Number(item && item.quantity) || 1),
            apc_hidden_addon: Boolean(privateProperties(item) || (item && item.product_type === 'Custom'))
          };
        });
        var visibleItem = physicalItems.find(function (item) {
          return !privateProperties(item) && item && item.product_type !== 'Custom';
        }) || physicalItems[0];
        context.variant_id = visibleItem && (visibleItem.variant_id || visibleItem.id);
        context.event_id = ['apc_add', session, now(), context.variant_id || 'unknown'].join('_');
        return context;
      }).catch(function () { return fallback; });
    } catch (_) {
      return Promise.resolve(fallback);
    }
  }
  function cartOperation(input) { var url = typeof input === 'string' ? input : (input && input.url) || ''; var m = url.match(/\/cart\/(add|change|update|clear)(?:\.js)?(?:\?|$)/); return m && m[1]; }

  /* Claim a fetch call only while wrapper layers synchronously delegate it. A
   * request object can be reused for later calls, so release it before the returned
   * promise settles; that preserves repeat-event measurement. */
  var claimed = (typeof window.WeakSet === 'function') ? new window.WeakSet() : null;
  function claim(input, init) {
    var key = init && typeof init === 'object'
      ? init
      : (input && typeof input === 'object' ? input : null);
    if (!claimed || !key) return { key: null, report: true };
    if (claimed.has(key)) return { key: null, report: false };
    claimed.add(key);
    return { key: key, report: true };
  }
  function releaseClaim(claimedCall) {
    if (claimed && claimedCall && claimedCall.key) claimed.delete(claimedCall.key);
  }

  /* Elevar reconciles /cart.js after each cart mutation and may include stale
   * pre-existing deltas in the same synchronous add burst. Scope customized
   * aggregation to the exact /cart/add request instead of globally batching
   * unrelated dl_add_to_cart events. */
  var customizedAdds = [];
  function requestItems(init) {
    try {
      var body = init && init.body;
      if (typeof body !== 'string') return [];
      var payload = JSON.parse(body);
      return Array.isArray(payload && payload.items) ? payload.items : [];
    } catch (_) {
      return [];
    }
  }
  function requestItemVariantId(item) {
    return String((item && (item.variant_id || item.id)) || '');
  }
  function requestItemQuantity(item) {
    return Math.max(1, Number(item && item.quantity) || 1);
  }
  function hiddenRequestItem(item) {
    return Boolean(item && item.properties && item.properties._cf_ === 'addonProduct');
  }
  function descriptorSignature(items, responseItems) {
    return items.map(function (item) {
      var id = responseItems
        ? String((item && item.item_variant_id) || '')
        : requestItemVariantId(item);
      var quantity = responseItems
        ? Math.max(1, Number(item && item.quantity) || 1)
        : requestItemQuantity(item);
      var hidden = responseItems
        ? Boolean(item && item.apc_hidden_addon)
        : hiddenRequestItem(item);
      return [id, quantity, hidden ? 'hidden' : 'visible'].join(':');
    }).sort().join('|');
  }
  function removeCustomizedAdd(descriptor) {
    var index = customizedAdds.indexOf(descriptor);
    if (index !== -1) customizedAdds.splice(index, 1);
    if (descriptor && descriptor.timer) window.clearTimeout(descriptor.timer);
    if (descriptor && descriptor.commitTimer) window.clearTimeout(descriptor.commitTimer);
  }
  function replayCustomizedAdd(descriptor) {
    if (!descriptor || descriptor.finished) return;
    descriptor.finished = true;
    var events = descriptor.events.slice(0);
    var layer = descriptor.layer;
    var originalPush = descriptor.originalPush;
    removeCustomizedAdd(descriptor);
    if (!layer || typeof originalPush !== 'function') return;
    events.forEach(function (event) { originalPush.call(layer, event); });
  }
  function createCustomizedAdd(init) {
    var items = requestItems(init);
    var visible = items.filter(function (item) { return !hiddenRequestItem(item); });
    var hidden = items.filter(hiddenRequestItem);
    if (visible.length !== 1 || !hidden.length) return null;
    var descriptor = {
      created: now(),
      expectedSignature: descriptorSignature(items, false),
      visibleVariantId: requestItemVariantId(visible[0]),
      events: [],
      context: null,
      finished: false,
      layer: null,
      originalPush: null,
      timer: null,
      commitTimer: null
    };
    customizedAdds.push(descriptor);
    descriptor.timer = window.setTimeout(function () { replayCustomizedAdd(descriptor); }, 2000);
    return descriptor;
  }
  function activeCustomizedAdds() {
    return customizedAdds.filter(function (descriptor) {
      return descriptor && !descriptor.finished && now() - descriptor.created < 2000;
    });
  }
  function beginCustomizedAdd(init) {
    var active = activeCustomizedAdds();
    if (active.length) {
      active.forEach(replayCustomizedAdd);
      return null;
    }
    return createCustomizedAdd(init);
  }
  function trackingVariantId(product) {
    return String((product && (product.variant_id || product.item_variant_id || product.variantId)) || '');
  }
  function addEventProducts(event) {
    var products = event && event.ecommerce && event.ecommerce.add && event.ecommerce.add.products;
    return Array.isArray(products) ? products : [];
  }
  function hasExpectedTrackingProducts(descriptor) {
    var seen = Object.create(null);
    descriptor.events.forEach(function (event) {
      addEventProducts(event).forEach(function (product) {
        var id = trackingVariantId(product);
        if (id) seen[id] = true;
      });
    });
    var expectedIds = descriptor.expectedSignature.split('|').map(function (part) { return part.split(':')[0]; });
    return expectedIds.every(function (id) { return Boolean(id && seen[id]); });
  }
  function matchesExpectedTrackingProduct(descriptor, event) {
    var expected = Object.create(null);
    descriptor.expectedSignature.split('|').forEach(function (part) {
      var id = part.split(':')[0];
      if (id) expected[id] = true;
    });
    return addEventProducts(event).some(function (product) {
      return Boolean(expected[trackingVariantId(product)]);
    });
  }
  function sameTypeNumber(value, template, fixed) {
    if (typeof template === 'string') return fixed ? Number(value).toFixed(2) : String(value);
    return Number(value);
  }
  function commitCustomizedAdd(descriptor) {
    if (!descriptor || descriptor.finished || !descriptor.context) return;
    var context = descriptor.context;
    var visibleResponse = context.items && context.items.find(function (item) {
      return !item.apc_hidden_addon && String(item.item_variant_id || '') === descriptor.visibleVariantId;
    });
    var baseEvent = descriptor.events.find(function (event) {
      return addEventProducts(event).some(function (product) {
        return trackingVariantId(product) === descriptor.visibleVariantId;
      });
    });
    var baseProduct = baseEvent && addEventProducts(baseEvent).find(function (product) {
      return trackingVariantId(product) === descriptor.visibleVariantId;
    });
    var quantity = Math.max(1, Number(visibleResponse && visibleResponse.quantity) || 1);
    var total = Number(context.value);
    if (!baseEvent || !baseProduct || !Number.isFinite(total) || total <= 0) {
      replayCustomizedAdd(descriptor);
      return;
    }
    var mergedProduct = Object.assign({}, baseProduct);
    mergedProduct.price = sameTypeNumber(total / quantity, baseProduct.price, true);
    mergedProduct.quantity = sameTypeNumber(quantity, baseProduct.quantity, false);
    if (Object.prototype.hasOwnProperty.call(mergedProduct, 'value')) {
      mergedProduct.value = sameTypeNumber(total, baseProduct.value, true);
    }
    mergedProduct.apc_customized_product = true;
    mergedProduct.apc_component_count = context.items.length;
    var mergedEvent = Object.assign({}, baseEvent);
    mergedEvent.ecommerce = Object.assign({}, baseEvent.ecommerce, {
      currencyCode: context.currency || (baseEvent.ecommerce && baseEvent.ecommerce.currencyCode)
    });
    mergedEvent.ecommerce.add = Object.assign({}, baseEvent.ecommerce && baseEvent.ecommerce.add, {
      products: [mergedProduct]
    });
    descriptor.finished = true;
    var layer = descriptor.layer;
    var originalPush = descriptor.originalPush;
    removeCustomizedAdd(descriptor);
    originalPush.call(layer, mergedEvent);
  }
  function maybeCommitCustomizedAdd(descriptor) {
    if (!descriptor || descriptor.finished || descriptor.commitTimer || !descriptor.context) return;
    if (!hasExpectedTrackingProducts(descriptor)) return;
    descriptor.commitTimer = window.setTimeout(function () {
      descriptor.commitTimer = null;
      commitCustomizedAdd(descriptor);
    }, 0);
  }
  function completeCustomizedAdd(descriptor, context) {
    if (!descriptor || descriptor.finished) return;
    var responseItems = context && Array.isArray(context.items) ? context.items : [];
    if (!responseItems.length || descriptorSignature(responseItems, true) !== descriptor.expectedSignature) {
      replayCustomizedAdd(descriptor);
      return;
    }
    descriptor.context = context;
    maybeCommitCustomizedAdd(descriptor);
  }

  function notifyFailure(event) {
    if (!event || event.outcome === 'success') return;
    // This is an alert signal, not a cart dependency. It contains no cart,
    // customer, or line-item-property data and any failure is intentionally ignored.
    try {
      var payload = JSON.stringify({ operation: event.operation, outcome: event.outcome, status: event.status, theme_build: event.theme_build, session: event.session, variant_id: event.variant_id, item_count: event.item_count });
      var url = 'https://inbox-push.atlaspetcompany.com/cart-telemetry';
      if (window.navigator && typeof window.navigator.sendBeacon === 'function' && typeof window.Blob === 'function') {
        window.navigator.sendBeacon(url, new window.Blob([payload], { type: 'text/plain' }));
      } else if (window.fetch) {
        window.fetch(url, { method: 'POST', mode: 'cors', keepalive: true, headers: { 'Content-Type': 'text/plain' }, body: payload }).catch(function () {});
      }
    } catch (_) {}
  }
  function emit(operation, outcome, status, context) {
    try {
      var event = { event: 'apc_cart', name: 'apc_cart', operation: operation, outcome: outcome, status: status || 0, theme_build: build, session: session, event_id: context && context.event_id, variant_id: context && context.variant_id, item_count: context && context.item_count, line_count: context && context.line_count, currency: context && context.currency, value: context && context.value, items: context && context.items };
      // Reuse the container's existing event_properties data-layer variables for
      // the staged Google Ads replacement. The dedicated readiness marker is
      // absent from older theme builds, keeping that trigger dormant on MAIN.
      if (operation === 'add' && outcome === 'success' && context && Number.isFinite(Number(context.value))) {
        event.apc_google_ads_ready = true;
        event.event_properties = { value: Number(context.value), currency_code: context.currency || activeCurrency() };
      }
      // Analytics needs true counts, so every event reaches dataLayer and Clarity.
      window.dataLayer = window.dataLayer || []; window.dataLayer.push(event);
      if (typeof window.clarity === 'function') { window.clarity('event', 'cart_' + operation + '_' + outcome); window.clarity('set', 'cart_operation', operation); window.clarity('set', 'cart_outcome', outcome); window.clarity('set', 'cart_status', String(event.status)); window.clarity('set', 'cart_build', build); window.clarity('set', 'cart_session', session); }
      // The outbound failure beacon stays rate limited: alert channel, not analytics.
      var key = [operation, outcome, status || 0, event.variant_id || ''].join(':');
      if (outcome !== 'success' && allowed(key)) notifyFailure(event);
    } catch (_) {}
  }
  function installFetch() { if (!window.fetch || window.fetch.__apcCartTelemetry) return; var nativeFetch = window.fetch; function observed(input, init) { var op = cartOperation(input); var context = bodyContext(init); var claimedCall = Boolean(op) ? claim(input, init) : { key: null, report: false }; var customizedAdd = claimedCall.report && op === 'add' ? beginCustomizedAdd(init) : null; var promise; try { promise = nativeFetch.apply(this, arguments); } catch (error) { if (customizedAdd) replayCustomizedAdd(customizedAdd); if (claimedCall.report) emit(op, 'exception', 0, context); throw error; } finally { releaseClaim(claimedCall); } if (claimedCall.report && promise && typeof promise.then === 'function') { try { promise.then(function (response) { var outcome = response && response.ok ? 'success' : 'http_error'; if (op === 'add' && outcome === 'success') { enrichAddContext(response, context).then(function (enriched) { if (customizedAdd) completeCustomizedAdd(customizedAdd, enriched); emit(op, outcome, response && response.status, enriched); }); } else { if (customizedAdd) replayCustomizedAdd(customizedAdd); emit(op, outcome, response && response.status, context); } }, function () { if (customizedAdd) replayCustomizedAdd(customizedAdd); emit(op, 'network_error', 0, context); }); } catch (_) { if (customizedAdd) replayCustomizedAdd(customizedAdd); } } return promise; } observed.__apcCartTelemetry = true; window.fetch = observed; }
  function installXHR() { var proto = window.XMLHttpRequest && window.XMLHttpRequest.prototype; if (!proto || proto.__apcCartTelemetry) return; var open = proto.open, send = proto.send; proto.open = function (method, url) { this.__apcCartOperation = cartOperation(url); return open.apply(this, arguments); }; proto.send = function (body) { var xhr = this, op = xhr.__apcCartOperation; if (op) { if (op === 'add') activeCustomizedAdds().forEach(replayCustomizedAdd); var context = bodyContext({ body: body }); xhr.addEventListener('loadend', function () { var outcome = xhr.status === 0 ? 'network_error' : (xhr.status >= 200 && xhr.status < 300 ? 'success' : 'http_error'); emit(op, outcome, xhr.status, context); }, { once: true }); } return send.apply(this, arguments); }; proto.__apcCartTelemetry = true; }
  function installIntegrityListener() { if (installIntegrityListener.done) return; installIntegrityListener.done = true; document.addEventListener('apc:cart-integrity-failure', function (event) { var detail = event && event.detail || {}; emit(detail.operation || 'add', 'exception', 0, { variant_id: detail.reason || 'unknown' }); }); }
  function installElevarAddMerge() {
    var layer = window.ElevarDataLayer;
    if (!Array.isArray(layer) || !Object.prototype.hasOwnProperty.call(layer, 'push')) return;
    if (layer.push && layer.push.__apcCustomizedAddMerge) return;
    var originalPush = layer.push;
    if (typeof originalPush !== 'function') return;
    function mergedPush() {
      var args = Array.prototype.slice.call(arguments);
      args.forEach(function (event) {
        if (!event || event.event !== 'dl_add_to_cart') { originalPush.call(layer, event); return; }
        var active = activeCustomizedAdds();
        if (!active.length) { originalPush.call(layer, event); return; }
        if (active.length !== 1) {
          active.forEach(replayCustomizedAdd);
          originalPush.call(layer, event);
          return;
        }
        var descriptor = active[0];
        if (!matchesExpectedTrackingProduct(descriptor, event)) {
          originalPush.call(layer, event);
          return;
        }
        descriptor.layer = layer;
        descriptor.originalPush = originalPush;
        descriptor.events.push(event);
        maybeCommitCustomizedAdd(descriptor);
      });
      return layer.length;
    }
    mergedPush.__apcCustomizedAddMerge = true;
    layer.push = mergedPush;
  }
  function installTrackingMergers() {
    try { installElevarAddMerge(); } catch (_) {}
  }
  // Third-party storefront scripts may replace window.fetch after this asset
  // loads. Re-attach during page settle so cart failures remain observable.
  function installObservers() { try { installFetch(); installXHR(); installIntegrityListener(); installTrackingMergers(); } catch (_) {} }
  installObservers();
  if (typeof window.setTimeout === 'function') {
    [0, 100, 500, 1500, 5000, 15000].forEach(function (delay) {
      window.setTimeout(installObservers, delay);
    });
  }

  function device() {
    return window.matchMedia && window.matchMedia('(max-width: 767px)').matches ? 'mobile' : 'desktop';
  }
  function sourceFor(target) {
    if (target && target.closest && target.closest('cart-items[data-cart-drawer], cart-element[data-cart-drawer]')) return 'drawer';
    if (window.location.pathname.indexOf('/cart') === 0) return 'cart_page';
    return 'product_page';
  }
  var lastKnownCart = null;
  function pushCanonical(names, source, cart) {
    if (typeof names === 'string') names = [names];
    var hasCart = cart && typeof cart.total_price !== 'undefined';
    window.dataLayer = window.dataLayer || [];
    names.forEach(function (name) {
      window.dataLayer.push({
        event: name,
        cart_value: hasCart ? Number(cart.total_price || 0) / 100 : null,
        cart_value_cents: hasCart ? Number(cart.total_price || 0) : null,
        item_count: hasCart ? Number(cart.item_count || 0) : null,
        device: device(),
        source: source,
        theme_build: build
      });
      if (typeof window.clarity === 'function') window.clarity('event', name);
    });
  }
  /* One cart read, N dataLayer events. Checkout events are pushed synchronously
   * before navigation, then the fetch only refreshes the local cart snapshot. */
  function emitCanonical(names, source, immediate) {
    if (immediate) pushCanonical(names, source, lastKnownCart);
    return fetch(root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (response) { return response.ok ? response.json() : null; })
      .catch(function () { return null; })
      .then(function (cart) {
        if (cart) lastKnownCart = cart;
        if (!immediate) pushCanonical(names, source, cart);
        return cart;
      });
  }
  document.addEventListener('theme:cart:open', function () {
    emitCanonical(['cart_open', 'view_cart'], 'drawer');
  });

  var lastCheckout = 0;
  function trackCheckout(target) {
    var time = now();
    if (time - lastCheckout < 1000) return;
    lastCheckout = time;
    emitCanonical(['checkout_click', 'begin_checkout'], sourceFor(target), true);
  }

  document.addEventListener('click', function (event) {
    var checkout = event.target && event.target.closest && event.target.closest('[name="checkout"], [data-additional-checkout-button] button, [data-additional-checkout-button] a');
    if (checkout) trackCheckout(checkout);
  }, true);
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || form.nodeName !== 'FORM' || !form.querySelector('[name="checkout"]')) return;
    trackCheckout(form);
  }, true);
  if (window.location.pathname.indexOf('/cart') === 0) {
    emitCanonical(['view_cart'], 'cart_page');
  }
})(window, document);
